import React, { useState, useEffect } from 'react';
import { BookOpen, Clock, Users, Play, CheckCircle, Target, Filter, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import CourseViewer from './CourseViewer';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CourseList = ({ user }) => {
  const [courses, setCourses] = useState([]);
  const [assignedCourses, setAssignedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignCourse, setAssignCourse] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const canAssign = ['manager', 'team_lead', 'superadmin', 'ceo'].includes(user.role);

  useEffect(() => {
    fetchCourses();
    fetchAssignedCourses();
    if (canAssign) {
      fetchTeamMembers();
    }
  }, []);

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to fetch courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/courses/assigned`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignedCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch assigned courses');
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data.filter(u => u.role !== 'educator'));
    } catch (error) {
      console.error('Failed to fetch team members');
    }
  };

  const handleAssignCourse = async () => {
    if (!assignCourse || selectedUsers.length === 0) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/courses/assign`, {
        course_id: assignCourse.id,
        user_ids: selectedUsers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Course assigned to ${selectedUsers.length} user(s)`);
      setShowAssignModal(false);
      setAssignCourse(null);
      setSelectedUsers([]);
    } catch (error) {
      toast.error('Failed to assign course');
    }
  };

  const getProgressPercentage = (courseId) => {
    const assigned = assignedCourses.find(a => a.course_id === courseId);
    if (!assigned?.progress) return 0;
    
    const course = courses.find(c => c.id === courseId) || assigned.course;
    if (!course?.lessons?.length) return 0;
    
    return Math.round((assigned.progress.completed_lessons?.length || 0) / course.lessons.length * 100);
  };

  const isAssigned = (courseId) => {
    return assignedCourses.some(a => a.course_id === courseId);
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || course.category === filter;
    return matchesSearch && matchesFilter;
  });

  if (selectedCourse) {
    return (
      <CourseViewer 
        course={selectedCourse} 
        user={user}
        onBack={() => {
          setSelectedCourse(null);
          fetchAssignedCourses();
        }}
      />
    );
  }

  if (loading) return <div>Loading...</div>;

  return (
    <div className="management-page">
      <div className="page-header">
        <h2>Courses</h2>
      </div>

      {/* Assigned Courses Section */}
      {assignedCourses.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          <div style={{ 
            padding: '14px 20px',
            background: '#E7EDF9',
            borderRadius: '10px',
            marginBottom: '16px'
          }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
              📋 Assigned to You
            </h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
            {assignedCourses.map(assignment => (
              <div 
                key={assignment.id} 
                className="card" 
                style={{ padding: '20px', cursor: 'pointer', border: '2px solid var(--color-primary)' }}
                onClick={() => setSelectedCourse(assignment.course)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, fontSize: '16px', color: 'var(--text-primary)' }}>{assignment.course?.title}</h4>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '600',
                    background: assignment.status === 'completed' ? 'rgba(16, 185, 129, 0.1)' : 
                               assignment.status === 'in_progress' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                    color: assignment.status === 'completed' ? 'var(--color-success)' : 
                           assignment.status === 'in_progress' ? 'var(--color-warning)' : 'var(--color-info)'
                  }}>
                    {assignment.status.replace('_', ' ')}
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Progress</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                      {getProgressPercentage(assignment.course_id)}%
                    </span>
                  </div>
                  <div style={{ height: '6px', background: 'var(--bg-secondary)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ 
                      height: '100%', 
                      width: `${getProgressPercentage(assignment.course_id)}%`,
                      background: 'var(--color-primary)',
                      borderRadius: '3px',
                      transition: 'width 0.3s'
                    }} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  <span><Clock size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    {assignment.course?.total_duration_minutes} min
                  </span>
                  <span><BookOpen size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    {assignment.course?.lessons?.length || 0} lessons
                  </span>
                </div>

                {assignment.due_date && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--color-warning)' }}>
                    Due: {new Date(assignment.due_date).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '400px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search courses..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ paddingLeft: '40px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setFilter('all')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: filter === 'all' ? 'var(--color-primary)' : 'var(--bg-secondary)',
              color: filter === 'all' ? 'white' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            All
          </button>
          <button
            onClick={() => setFilter('wellness')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: filter === 'wellness' ? 'var(--color-success)' : 'var(--bg-secondary)',
              color: filter === 'wellness' ? 'white' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            🧘 Wellness
          </button>
          <button
            onClick={() => setFilter('skill_based')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: filter === 'skill_based' ? 'var(--color-secondary)' : 'var(--bg-secondary)',
              color: filter === 'skill_based' ? 'white' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            📚 Skill Based
          </button>
        </div>
      </div>

      {/* All Courses */}
      <div style={{ 
        padding: '14px 20px',
        background: '#E7EDF9',
        borderRadius: '10px',
        marginBottom: '16px'
      }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
          📚 All Courses
        </h3>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
        {filteredCourses.length === 0 ? (
          <div className="card" style={{ padding: '40px', textAlign: 'center', gridColumn: '1 / -1' }}>
            <BookOpen size={48} style={{ color: 'var(--text-muted)', marginBottom: '16px' }} />
            <p style={{ color: 'var(--text-tertiary)' }}>No courses found.</p>
          </div>
        ) : (
          filteredCourses.map(course => (
            <div key={course.id} className="card" style={{ padding: '0', overflow: 'hidden' }}>
              {/* Thumbnail */}
              <div style={{ 
                height: '140px', 
                background: course.category === 'wellness' 
                  ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' 
                  : 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {course.thumbnail_url ? (
                  <img src={course.thumbnail_url} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <BookOpen size={48} color="white" />
                )}
              </div>

              <div style={{ padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <h4 style={{ margin: 0, fontSize: '16px', color: 'var(--text-primary)', flex: 1 }}>{course.title}</h4>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '600',
                    background: course.category === 'wellness' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                    color: course.category === 'wellness' ? 'var(--color-success)' : 'var(--color-secondary)',
                    whiteSpace: 'nowrap',
                    marginLeft: '8px'
                  }}>
                    {course.category === 'wellness' ? 'Wellness' : 'Skill'}
                  </span>
                </div>

                <p style={{ 
                  margin: '0 0 12px 0', 
                  fontSize: '13px', 
                  color: 'var(--text-tertiary)',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {course.description}
                </p>

                <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  <span><Clock size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{course.total_duration_minutes} min</span>
                  <span><BookOpen size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{course.lessons?.length || 0} lessons</span>
                  <span><Users size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{course.completion_count}</span>
                </div>

                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                  By {course.creator_name}
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    className="gradient-btn" 
                    style={{ flex: 1, padding: '10px', fontSize: '13px' }}
                    onClick={() => setSelectedCourse(course)}
                  >
                    <Play size={14} /> {isAssigned(course.id) ? 'Continue' : 'Start Course'}
                  </button>
                  {canAssign && (
                    <button 
                      className="btn-secondary"
                      style={{ padding: '10px', fontSize: '13px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setAssignCourse(course);
                        setShowAssignModal(true);
                      }}
                    >
                      <Target size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Assign Course Modal */}
      {showAssignModal && assignCourse && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Assign Course</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Assign "{assignCourse.title}" to team members
            </p>

            <div className="form-group">
              <label>Select Team Members</label>
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                {teamMembers.map(member => (
                  <label 
                    key={member.id} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      padding: '12px',
                      borderBottom: '1px solid var(--border-color)',
                      cursor: 'pointer'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(member.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers(prev => [...prev, member.id]);
                        } else {
                          setSelectedUsers(prev => prev.filter(id => id !== member.id));
                        }
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{member.full_name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{member.role}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
              <button 
                className="gradient-btn" 
                onClick={handleAssignCourse}
                disabled={selectedUsers.length === 0}
              >
                Assign to {selectedUsers.length} User(s)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseList;
